<script lang="ts">
  import { useForm, page, Link } from '@inertiajs/svelte';

  interface Props {
    errors?: {
      base?: string[];
      email_address?: string[];
      password?: string[];
    };
  }

  let { errors: propsErrors }: Props = $props();

  const form = useForm({
    email_address: '',
    password: '',
    remember_me: false,
  });

  // Combine errors from props (server validation) and form (Inertia form errors)
  let errors = $derived(propsErrors || {});

  function submit(e: Event) {
    e.preventDefault();
    $form.post('/session');
  }
</script>

<div class="flex min-h-screen flex-col justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
  <div class="sm:mx-auto sm:w-full sm:max-w-md">
    <!-- Logo/Brand -->
    <div class="text-center">
      <h1 class="text-3xl font-bold text-gray-900">Momentium</h1>
      <p class="mt-2 text-sm text-gray-600">Plan your projects month by month</p>
    </div>

    <h2 class="mt-8 text-center text-2xl font-semibold text-gray-900">Sign in to your account</h2>
  </div>

  <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
    <div class="border border-gray-100 bg-white px-4 py-8 shadow-lg sm:rounded-xl sm:px-10">
      <!-- Base errors (invalid credentials, rate limiting) -->
      {#if errors?.base}
        <div class="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {#each errors.base as error}
            <p>{error}</p>
          {/each}
        </div>
      {/if}

      <!-- Flash messages -->
      {#if $page.props.flash?.notice}
        <div
          class="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700"
        >
          {$page.props.flash.notice}
        </div>
      {/if}

      {#if $page.props.flash?.alert}
        <div
          class="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-700"
        >
          {$page.props.flash.alert}
        </div>
      {/if}

      <form onsubmit={submit} class="space-y-6">
        <!-- Email -->
        <div>
          <label for="email" class="block text-sm font-medium text-gray-700"> Email address </label>
          <input
            type="email"
            id="email"
            name="email_address"
            autocomplete="email"
            bind:value={$form.email_address}
            class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            required
          />
          {#if errors?.email_address}
            <p class="mt-1 text-sm text-red-600">{errors.email_address.join(', ')}</p>
          {/if}
        </div>

        <!-- Password -->
        <div>
          <label for="password" class="block text-sm font-medium text-gray-700"> Password </label>
          <input
            type="password"
            id="password"
            name="password"
            autocomplete="current-password"
            bind:value={$form.password}
            class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            required
          />
          {#if errors?.password}
            <p class="mt-1 text-sm text-red-600">{errors.password.join(', ')}</p>
          {/if}
        </div>

        <!-- Remember me / forgot password -->
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <input
              type="checkbox"
              id="remember_me"
              name="remember_me"
              value="1"
              bind:checked={$form.remember_me}
              class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label for="remember_me" class="ml-2 block text-sm text-gray-700">
              Keep me signed in
            </label>
          </div>

          <Link
            href="/passwords/new"
            class="text-sm text-blue-600 hover:text-blue-500 hover:underline"
          >
            Forgot your password?
          </Link>
        </div>

        <!-- Submit button -->
        <button
          type="submit"
          disabled={$form.processing}
          class="flex w-full justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          {#if $form.processing}
            <svg
              class="mr-2 -ml-1 h-4 w-4 animate-spin text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Signing in...
          {:else}
            Sign in
          {/if}
        </button>
      </form>

      <!-- Sign up link -->
      <div class="mt-6">
        <div class="relative">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-gray-200"></div>
          </div>
          <div class="relative flex justify-center text-sm">
            <span class="bg-white px-2 text-gray-500">New to Momentium?</span>
          </div>
        </div>

        <div class="mt-6">
          <Link
            href="/signup"
            class="flex w-full justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
          >
            Create an account
          </Link>
        </div>
      </div>
    </div>
  </div>
</div>
